import React, { useState, useEffect } from 'react';
import {
  FiSearch,
  FiUser,
  FiMoreVertical,
  FiEye,
  FiLock,
  FiUnlock
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await api.getAdminUsers({ page: pagination.page, limit: 20 });
      setUsers(data.users || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.updateUserStatus(userId, !currentStatus);
      setUsers(users.map(u =>
        u._id === userId ? { ...u, isActive: !currentStatus } : u
      ));
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error(error.message);
    }
    setActiveMenu(null);
  };

  const filteredUsers = users.filter(u =>
    u.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    searchWrapper: {
      position: 'relative',
      marginBottom: '24px',
      maxWidth: '400px'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#555'
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 44px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '16px',
      borderBottom: '1px solid #eee',
      color: '#555',
      fontWeight: '500',
      fontSize: '0.875rem'
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #f5f5f5',
      verticalAlign: 'middle'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    avatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#555'
    },
    userName: {
      fontWeight: '500'
    },
    userEmail: {
      color: '#555',
      fontSize: '0.8125rem'
    },
    badge: {
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    badgeActive: {
      background: '#E8F5E9',
      color: '#2E7D32'
    },
    badgeInactive: {
      background: '#FFEBEE',
      color: '#C62828'
    },
    actions: {
      position: 'relative'
    },
    actionBtn: {
      padding: '8px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      borderRadius: '4px'
    },
    dropdown: {
      position: 'absolute',
      right: 0,
      top: '100%',
      background: 'white',
      border: '1px solid #eee',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 10,
      minWidth: '150px'
    },
    dropdownItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      border: 'none',
      background: 'none',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      borderTop: '1px solid #eee'
    },
    pageInfo: {
      color: '#555',
      fontSize: '0.875rem'
    },
    pageButtons: {
      display: 'flex',
      gap: '8px'
    },
    pageBtn: {
      padding: '8px 16px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      background: 'white',
      cursor: 'pointer'
    },
    empty: {
      textAlign: 'center',
      padding: '60px',
      color: '#555'
    },
    // User detail modal
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px'
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '500px'
    },
    modalHeader: {
      padding: '24px',
      borderBottom: '1px solid #eee',
      textAlign: 'center'
    },
    modalAvatar: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      fontSize: '2rem',
      color: '#555'
    },
    modalName: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '4px'
    },
    modalEmail: {
      color: '#555'
    },
    modalBody: {
      padding: '24px'
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid #f5f5f5'
    },
    detailLabel: {
      color: '#555'
    },
    detailValue: {
      fontWeight: '500'
    },
    modalFooter: {
      padding: '16px 24px',
      borderTop: '1px solid #eee',
      textAlign: 'right'
    },
    closeBtn: {
      padding: '10px 24px',
      background: '#f5f5f5',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500'
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Users</h1>
      </header>

      <div style={styles.searchWrapper}>
        <FiSearch style={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search users..."
          style={styles.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div style={styles.card}>
        {filteredUsers.length > 0 ? (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Bookings</th>
                  <th style={styles.th}>Joined</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user._id}>
                    <td style={styles.td}>
                      <div style={styles.userInfo}>
                        <div style={styles.avatar}>
                          <FiUser />
                        </div>
                        <div>
                          <div style={styles.userName}>
                            {user.firstName} {user.lastName}
                          </div>
                          <div style={styles.userEmail}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{user.phone || '-'}</td>
                    <td style={styles.td}>{user.bookingsCount || 0}</td>
                    <td style={styles.td}>
                      {new Date(user.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          ...(user.isActive !== false ? styles.badgeActive : styles.badgeInactive)
                        }}
                      >
                        {user.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => setActiveMenu(activeMenu === user._id ? null : user._id)}
                        >
                          <FiMoreVertical />
                        </button>
                        {activeMenu === user._id && (
                          <div style={styles.dropdown}>
                            <button
                              style={styles.dropdownItem}
                              onClick={() => {
                                setSelectedUser(user);
                                setActiveMenu(null);
                              }}
                            >
                              <FiEye /> View Details
                            </button>
                            <button
                              style={styles.dropdownItem}
                              onClick={() => toggleUserStatus(user._id, user.isActive !== false)}
                            >
                              {user.isActive !== false ? <FiLock /> : <FiUnlock />}
                              {user.isActive !== false ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.pages > 1 && (
              <div style={styles.pagination}>
                <div style={styles.pageInfo}>
                  Showing {(pagination.page - 1) * 20 + 1} - {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
                </div>
                <div style={styles.pageButtons}>
                  <button
                    style={styles.pageBtn}
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <button
                    style={styles.pageBtn}
                    disabled={pagination.page === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={styles.empty}>
            <FiUser size={48} style={{ marginBottom: '16px', color: '#ddd' }} />
            <p>No users found</p>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div style={styles.modal} onClick={() => setSelectedUser(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalAvatar}>
                <FiUser />
              </div>
              <h2 style={styles.modalName}>
                {selectedUser.firstName} {selectedUser.lastName}
              </h2>
              <p style={styles.modalEmail}>{selectedUser.email}</p>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Phone</span>
                <span style={styles.detailValue}>{selectedUser.phone || 'Not provided'}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Total Bookings</span>
                <span style={styles.detailValue}>{selectedUser.bookingsCount || 0}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Saved Apartments</span>
                <span style={styles.detailValue}>{selectedUser.savedApartments?.length || 0}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Joined</span>
                <span style={styles.detailValue}>
                  {new Date(selectedUser.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Status</span>
                <span
                  style={{
                    ...styles.badge,
                    ...(selectedUser.isActive !== false ? styles.badgeActive : styles.badgeInactive)
                  }}
                >
                  {selectedUser.isActive !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.closeBtn} onClick={() => setSelectedUser(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
